import { CSSProperties } from 'react';
import { imgPath } from '../../common/utilities/ImgUrl';
import { ISize } from '../models/ISize';
import { Module } from '../Module';
import { IResult } from '../ServerApi/types/IResponse';
import { ICustomLogo, IServerCustomization } from '../ServerApi/types/IServerSettingsUnauthenticated';
import { ServerService } from './ServerService';
import { SessionService } from './SessionService';
import { StartPageService } from './StartPageService';

export type LogoType = 'header' | 'compact' | 'signin';

export interface ILogo {
  image: string;
  width: number;
  height: number;
  href: string;
  title: string;
  styleOverrides?: CSSProperties;
}

export class LogoService {

  // TODO: Should imgPath incorporate the static asset prefix?
  private static DefaultSignInLogoImage = StaticAssetsUrlPrefix + imgPath('tableau_sign_in_logo.svg');
  private static DefaultSignInLogoWidth = 277;
  private static DefaultSignInLogoHeight = 58;

  // default logo
  private static DefaultNavPanelLogo = imgPath('Tableau_Logo_Mark.svg');
  private static DefaultNavPanelLogoWidth = 40;
  private static DefaultNavPanelLogoHeight = 40;

  // compact logo
  private static DefaultCompactLogo = imgPath('Tableau_Logo_Mark.svg');
  private static DefaultCompactLogoWidth = 24;
  private static DefaultCompactLogoHeight = 24;

  // These maximums are known by tabadmin
  private static MaximumHeaderLogoWidth = 160;
  private static MaximumHeaderLogoHeight = 160;
  private static MaximumSignInLogoWidth = 3000;
  private static MaximumSignInLogoHeight = 3000;
  private static MaximumCompactLogoWidth = 32;
  private static MaximumCompactLogoHeight = 32;

  private serverCustomization: IServerCustomization;
  private getSiteHeaderLogo: () => ICustomLogo;
  private getSiteCompactLogo: () => ICustomLogo;
  private isSiteCustomizationEnabled: () => boolean;

  public static $inject = [
    'ServerService',
    'StartPageService',
    'SessionService'
  ];

  constructor(
    private server: ServerService,
    private startPageService: StartPageService,
    private sessionService: SessionService
  ) {
    this.serverCustomization = server.serverCustomization();
    this.getSiteHeaderLogo = () => server.siteLogo();
    this.getSiteCompactLogo = () => server.compactLogo();
    this.isSiteCustomizationEnabled = () => server.isSiteCustomizationEnabled();
  }

  /**
   * Call the updateSiteLogo web client REST API to update a site logo.
   * @param file      the file to set as the new logo, or falsey to reset the logo
   * @param logoType  which logo to update
   */
  public updateSiteLogo(file: File, logoType: LogoType): ng.IPromise<IResult> {
    const formData = new FormData();
    if (file) {
      formData.append('updateSiteCustomLogoInput', file);
    }
    formData.append(this.sessionService.getSessionCookieName(), this.sessionService.getSessionId());
    formData.append('useCustomLogo', file ? 'true' : 'false');
    formData.append('useIframeResponse', 'false');
    formData.append('logoType', logoType);
    return this.server.requestPostFormToRestApi(formData, 'updateSiteLogo');
  }

  public isUsingCustomSiteLogo(): boolean {
    return this.isSiteCustomizationEnabled() && !!this.getSiteHeaderLogo();
  }

  public isUsingCustomServerLogo(): boolean {
    return !!(this.serverCustomization && this.serverCustomization.customHeaderLogo);
  }

  public isUsingCustomCompactSiteLogo(): boolean {
    return this.isSiteCustomizationEnabled() && !!this.getSiteCompactLogo();
  }

  public isUsingCustomCompactServerLogo(): boolean {
    return !!(this.serverCustomization && this.serverCustomization.customCompactLogo);
  }

  public getNavPanelLogo(): ILogo {
    if (this.isUsingCustomSiteLogo()) {
      return this.getCustomLogo(
        this.getSiteHeaderLogo(),
        LogoService.MaximumHeaderLogoWidth,
        LogoService.MaximumHeaderLogoHeight,
        this.server.sessionSiteName());
    } else {
      return this.getServerHeaderLogo();
    }
  }

  public getCompactLogo(): ILogo {
    if (this.isUsingCustomCompactSiteLogo() || this.isUsingCustomSiteLogo()) {
      return this.getCustomLogo(
        this.getSiteCompactLogo() || this.getSiteHeaderLogo(),
        LogoService.MaximumCompactLogoWidth,
        LogoService.MaximumCompactLogoHeight,
        this.server.sessionSiteName());
    } else {
      return this.getServerCompactLogo();
    }
  }

  public hasCustomCompactLogo(): boolean {
    return this.isUsingCustomCompactSiteLogo() || this.isUsingCustomCompactServerLogo();
  }

  public hasCustomHeaderLogo(): boolean {
    return this.isUsingCustomSiteLogo() || this.isUsingCustomServerLogo();
  }

  private getServerHeaderLogo(): ILogo {
    if (this.serverCustomization && this.serverCustomization.customHeaderLogo) {
      return this.getCustomLogo(
        this.serverCustomization.customHeaderLogo,
        LogoService.MaximumHeaderLogoWidth,
        LogoService.MaximumHeaderLogoHeight,
        this.server.serverName());
    } else {
      return this.getDefaultHeaderLogo();
    }
  }

  public getDefaultHeaderLogo(): ILogo {
    return {
      image:LogoService.DefaultNavPanelLogo,
      width: LogoService.DefaultNavPanelLogoWidth,
      height: LogoService.DefaultNavPanelLogoHeight,
      href: this.getStartPageHref(),
      title: this.server.serverName()
    };
  }

  private getServerCompactLogo(): ILogo {
    if (this.serverCustomization && this.serverCustomization.customCompactLogo) {
      return this.getCustomLogo(
        this.serverCustomization.customCompactLogo,
        LogoService.MaximumCompactLogoWidth,
        LogoService.MaximumCompactLogoHeight,
        this.server.serverName());

    } else if (this.serverCustomization && this.serverCustomization.customHeaderLogo) {
      // No custom compact logo but have a custom header logo-- use it, but resize it
      return this.getCustomLogo(
        this.serverCustomization.customHeaderLogo,
        LogoService.MaximumCompactLogoWidth,
        LogoService.MaximumCompactLogoHeight,
        this.server.serverName());

    } else {
      // no custom compact or header logo
      return this.getDefaultCompactLogo();
    }
  }

  public getDefaultCompactLogo(): ILogo {
    return {
      image:LogoService.DefaultCompactLogo,
      width: LogoService.DefaultCompactLogoWidth,
      height: LogoService.DefaultCompactLogoHeight,
      href: this.getStartPageHref(),
      title: this.server.serverName()
    };
  }

  public getSignInLogo(): ILogo {
    if (this.serverCustomization && this.serverCustomization.customSignInLogo) {
      return this.getCustomLogo(
        this.serverCustomization.customSignInLogo,
        LogoService.MaximumSignInLogoWidth,
        LogoService.MaximumSignInLogoHeight,
        this.server.serverName());
    } else {
      return {
        image: LogoService.DefaultSignInLogoImage,
        width: LogoService.DefaultSignInLogoWidth,
        height: LogoService.DefaultSignInLogoHeight,
        href: '', // No need for sign in
        title: this.server.serverName()
      };
    }
  }

  private getStartPageHref(): string {
    return '#' + this.startPageService.getCurrentUserStartPageForCurrentSite();
  }

  public getCustomLogo(logo: ICustomLogo, maxWidth: number, maxHeight: number, title: string): ILogo {
    const scaledSize = LogoService.getCustomLogoNavPanelAdjustedSize(logo, maxWidth, maxHeight);
    return {
      ...scaledSize,
      image: logo.path,
      href: logo.link || this.getStartPageHref(),
      title: title
    };
  }

  public static getScaledLogo(logo: ILogo, logoType: LogoType): ILogo {
    const maxWidth = this.getMaxWidthForLogoType(logoType);
    const maxHeight = this.getMaxHeightForLogoType(logoType);
    return {
      ...logo,
      ...LogoService.getCustomLogoNavPanelAdjustedSize(logo, maxWidth, maxHeight)
    };
  }

  public static getMaxWidthForLogoType(logoType: LogoType): number {
    switch (logoType) {
      case 'header':
        return LogoService.MaximumHeaderLogoWidth;
      case 'compact':
        return LogoService.MaximumCompactLogoWidth;
      case 'signin':
        return LogoService.MaximumSignInLogoWidth;
    }
    return 0;
  }

  public static getMaxHeightForLogoType(logoType: LogoType): number {
    switch (logoType) {
      case 'header':
        return LogoService.MaximumHeaderLogoHeight;
      case 'compact':
        return LogoService.MaximumCompactLogoHeight;
      case 'signin':
        return LogoService.MaximumSignInLogoHeight;
    }
    return 0;
  }

  public static getCustomLogoNavPanelAdjustedSize(logoDimensions: ISize, maxWidth: number, maxHeight: number): ISize {
    const width = logoDimensions.width;
    const height = logoDimensions.height;
    const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
    return { width: width * ratio, height: height * ratio };
  }
}

Module.instance().service('LogoService', LogoService);
